import { createSlice, PayloadAction } from "@reduxjs/toolkit";
// Types
import Reducers from "app/types/Reducers";
// Models
import ISnackbar from "app/models/Snackbar";
import ISystemMessage from "app/models/SystemMessage";
// Utilities
import { uuidv4 } from 'app/utilities/Utilities';

export interface IPushAlert {
  caseId?: number;
  documentId?: number;
  message: string;
}

interface IState {
  snackbars: ISnackbar[];
  systemMessages: ISystemMessage[];
  pushAlerts: {
    case: {
      labels: IPushAlert | null;
      notes: IPushAlert | null;
    },
    document: {
      labels: IPushAlert | null;
    }
  };
};

const initialState:IState = {
  snackbars: [],
  systemMessages: [],
  pushAlerts: {
    case: {
      labels: null,
      notes: null
    },
    document: {
      labels: null,
    }
  }
};

type PushAlertCategoryType = {
  case: keyof IState['pushAlerts']['case'];
  document: keyof IState['pushAlerts']['document'];
};


type AddPushAlertPayload = {
  [K in keyof PushAlertCategoryType]: {
    category: K;
    type: PushAlertCategoryType[K];
    alert: IPushAlert;
  }
}[keyof PushAlertCategoryType];

type RemovePushAlertPayload = {
  [K in keyof PushAlertCategoryType]: {
    category: K;
    type: PushAlertCategoryType[K];
  }
}[keyof PushAlertCategoryType];

const slice = createSlice({
  name: Reducers.AppUINotifications,
  initialState,
  reducers: {
    addSnackbar: (state, action:PayloadAction<ISnackbar>) => {
      const key = action.payload.key || uuidv4();
      state.snackbars = [
        ...state.snackbars,
        { ...action.payload, key }
      ];
    },
    closeSnackbar: (state, action:PayloadAction<{ key:string, dismissAll:boolean }>) => {
      state.snackbars = state.snackbars.map((snackbar:ISnackbar) =>
        (action.payload.dismissAll || snackbar.key === action.payload.key)
          ? { ...snackbar, dismissed: true }
          : snackbar
      )
    },
    removeSnackbar: (state, action:PayloadAction<string>) => {
      state.snackbars = state.snackbars.filter((snackbar:ISnackbar) => snackbar.key !== action.payload)
    },

    addSystemMessage: (state, action:PayloadAction<ISystemMessage>) => {
      const id = action.payload.id || uuidv4();
      state.systemMessages = [
        ...state.systemMessages,
        { ...action.payload, id }
      ];
    },
    removeSystemMessage: (state, action:PayloadAction<string>) => {
      state.systemMessages = state.systemMessages.filter((systemMessage:ISystemMessage) => {
        return systemMessage.id !== action.payload
      })
    },

    addPushAlert: (state: IState, action: PayloadAction<AddPushAlertPayload>) => {
      const { category, type, alert } = action.payload;
      (state.pushAlerts[category] as Record<string, IPushAlert | null>)[type] = alert;
    },
    removePushAlert: (state: IState, action: PayloadAction<RemovePushAlertPayload>) => {
      const { category, type } = action.payload;
      (state.pushAlerts[category] as Record<string, IPushAlert | null>)[type] = null;
    },

    setInitialField: <IStateKey extends keyof IState>(state:IState, action:PayloadAction<IStateKey>) => {
      state[action.payload] = initialState[action.payload];
    },
    resetState: () => initialState
  }
});

export const AppUiNotificationsActions = slice.actions;

export default slice.reducer;
